import { ArrowRight, Phone } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import { useContent } from "@/content/ContentContext";
import { Reveal } from "./Reveal";

const FOOT_LINKS = [
    { label: "Manifesto", hash: "#manifesto", testid: "footer-link-manifesto" },
    { label: "Coverage", hash: "#plans", testid: "footer-link-coverage" },
    { label: "The Difference", hash: "#compare", testid: "footer-link-compare" },
    { label: "For Dealers", hash: "#dealers", testid: "footer-link-dealers" },
    { label: "FAQ", hash: "#faq", testid: "footer-link-faq" },
];

export const Footer = ({ onQuote }) => {
    const content = useContent();

    return (
    <footer data-testid="footer-section" className="relative overflow-hidden">
        <div className="border-t border-white/10 bg-[#0C1019]">
            <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
                <Reveal className="flex flex-col items-start gap-10">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                        {content.footer_cta_eyebrow}
                    </p>
                    <h2 className="max-w-3xl font-display text-3xl font-bold leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
                        {content.footer_cta_heading_a}{" "}
                        <span className="text-slate-500">{content.footer_cta_heading_b}</span>
                    </h2>
                    <div className="flex flex-wrap items-center gap-5">
                        <button
                            data-testid="footer-get-quote-btn"
                            onClick={onQuote}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-8 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                        >
                            Get My Instant Quote
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <p className="text-sm text-slate-500">
                            {content.footer_cta_note}
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>

        <div className="border-t border-white/10">
            <div className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 lg:px-8">
                <div
                    className="pointer-events-none select-none text-center font-display text-[13.5vw] font-extrabold leading-[0.85] tracking-tight text-white/[0.04]"
                    aria-hidden="true"
                >
                    ADVANTA
                </div>

                <div className="mt-6 flex flex-col gap-10 border-t border-white/10 pt-10 md:flex-row md:items-start md:justify-between">
                    <div className="max-w-sm">
                        <img
                            src="/logo-stacked.webp"
                            alt="AdvantaShield Warranty"
                            width="900"
                            height="600"
                            loading="lazy"
                            decoding="async"
                            className="h-28 w-auto"
                        />
                        <p className="mt-4 text-sm leading-relaxed text-slate-500">
                            {content.footer_blurb}
                        </p>
                        <a
                            href="tel:+18886874889"
                            data-testid="footer-phone-link"
                            className="mt-5 inline-flex items-center gap-2.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:text-sky-300"
                        >
                            <Phone className="h-4 w-4 text-sky-400" />
                            (888) 687-4889
                        </a>
                    </div>

                    <nav className="flex flex-col gap-3">
                        {FOOT_LINKS.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={l.testid}
                                onClick={() => scrollToSection(l.hash)}
                                className="text-left text-sm text-slate-400 transition-colors duration-300 hover:text-sky-300"
                            >
                                {l.label}
                            </button>
                        ))}
                        <a
                            href="/buyers-guide"
                            data-testid="footer-link-guide"
                            className="text-left text-sm text-slate-400 transition-colors duration-300 hover:text-sky-300"
                        >
                            Buyer's Guide
                        </a>
                        <a
                            href="/blog"
                            data-testid="footer-link-blog"
                            className="text-left text-sm text-slate-400 transition-colors duration-300 hover:text-sky-300"
                        >
                            Blog
                        </a>
                        <a
                            href="/coverage"
                            data-testid="footer-link-states"
                            className="text-left text-sm text-slate-400 transition-colors duration-300 hover:text-sky-300"
                        >
                            Coverage by State
                        </a>
                    </nav>

                    <div className="max-w-md">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
                            Disclosure
                        </p>
                        <p className="mt-3 text-xs leading-relaxed text-slate-600">
                            Coverage is provided under a vehicle service contract, not
                            a manufacturer warranty. Benefits, terms, waiting periods,
                            and exclusions vary by plan and state. Eligibility depends
                            on vehicle age and mileage. Review your contract for
                            complete details before purchase.
                        </p>
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
                    <p className="text-xs text-slate-600">
                        © 2026 AdvantaShield. All rights reserved.
                    </p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-600">
                        Created by dealers, for dealers
                    </p>
                </div>
            </div>
        </div>
    </footer>
    );
};
